import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/utils/api";
import { BookOpen, Users, ClipboardCheck, AlertTriangle, ArrowRight, ShieldCheck, PenLine } from "lucide-react";
import useDocumentTitle from "@/hooks/useDocumentTitle";
import { useBranding } from "@/contexts/BrandingContext";

const Tile = ({ icon: Icon, label, value, sub, to, tone = "green", testId }) => (
  <Link to={to} className="group rounded-xl border border-border bg-white p-5 transition-[border-color,transform] duration-200 hover:-translate-y-0.5 hover:border-green-400" data-testid={testId}>
    <div className="flex items-center justify-between">
      <span className={`inline-flex h-9 w-9 items-center justify-center rounded-lg ${tone === "rose" ? "bg-rose-50 text-rose-600" : "bg-green-50 text-green-700"}`}><Icon size={18} /></span>
      <ArrowRight size={14} className="text-slate-300 transition-transform duration-200 group-hover:translate-x-1 group-hover:text-green-600" />
    </div>
    <p className="mt-4 font-heading text-3xl font-bold tracking-tight">{value ?? "—"}</p>
    <p className="text-sm font-medium text-slate-700">{label}</p>
    {sub && <p className="mt-0.5 text-xs text-muted-foreground">{sub}</p>}
  </Link>
);

export default function T4RDashboardPage() {
  const { lms_name, company_name } = useBranding();
  useDocumentTitle("Dashboard", `${lms_name} overview`, lms_name);
  const [s, setS] = useState(null);
  const [flagged, setFlagged] = useState([]);

  useEffect(() => {
    api.get("/lms/admin/stats").then(({ data }) => setS(data)).catch(() => setS({}));
    api.get("/lms/admin/attempts?flagged_only=true").then(({ data }) => setFlagged((data.attempts || []).slice(0, 5))).catch(() => {});
  }, []);

  return (
    <div className="space-y-8" data-testid="t4r-dashboard">
      <div>
        <p className="text-xs font-semibold uppercase tracking-[0.2em] text-green-700">{lms_name}</p>
        <h1 className="mt-1 font-heading text-3xl font-bold tracking-tight">Overview</h1>
        <p className="mt-1 flex items-center gap-1.5 text-sm text-muted-foreground"><ShieldCheck size={14} className="text-green-600" /> Fully separate from the {company_name} CRM — no learner or customer data is shared.</p>
      </div>
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-5">
        <Tile icon={BookOpen} label="Courses" value={s?.courses} sub={s && `${s.published_courses} published · ${s.lessons} lessons`} to="/t4r/courses" testId="t4r-stat-courses" />
        <Tile icon={Users} label="Learners" value={s?.learners} sub={s && `${s.active_learners} active · ${s.invited_learners} invited`} to="/t4r/learners" testId="t4r-stat-learners" />
        <Tile icon={ClipboardCheck} label="Quiz attempts" value={s?.attempts} sub={s && `${s.passed_attempts} passed`} to="/t4r/attempts?status=all" testId="t4r-stat-attempts" />
        <Tile icon={PenLine} label="Awaiting marking" value={s?.awaiting_marking} sub="Written answers to mark" to="/t4r/attempts?status=awaiting" testId="t4r-stat-awaiting" />
        <Tile icon={AlertTriangle} label="AI-flagged" value={s?.flagged_attempts} sub={s && `${s.confirmed_breaches} confirmed breach${s.confirmed_breaches === 1 ? "" : "es"}`} to="/t4r/attempts?status=flagged" tone="rose" testId="t4r-stat-flagged" />
      </div>
      <div className="rounded-xl border border-border bg-white">
        <div className="flex items-center justify-between border-b border-border px-5 py-3">
          <h2 className="font-heading text-base font-semibold">Latest AI-flagged attempts</h2>
          <Link to="/t4r/attempts?status=flagged" className="text-xs font-medium text-green-700 hover:underline" data-testid="t4r-view-all-attempts">View all</Link>
        </div>
        <ul className="divide-y divide-border">
          {flagged.map((a) => (
            <li key={a.id} className="flex items-center justify-between px-5 py-3 text-sm" data-testid={`t4r-flagged-${a.id}`}>
              <span className="font-medium">{a.learner_name}</span>
              <span className="text-xs text-muted-foreground">{new Date(a.submitted_at).toLocaleString()} · {a.score_pct}%</span>
            </li>
          ))}
          {flagged.length === 0 && <li className="px-5 py-8 text-center text-sm text-muted-foreground">Nothing flagged — all clear.</li>}
        </ul>
      </div>
    </div>
  );
}
